import { useMemo, useState } from 'react';

import {
  Badge,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  searchApi,
  SEARCH_ENTITY_TYPE_ORDER,
  type SearchEntityType,
  type SearchHit,
} from '@/shared/api/search';
import { Icon } from '@/shared/components/Icon';
import { ReadingState } from '@/shared/components/ReadingState';

import { renderHighlightedSnippet } from './searchHighlight';
import { hrefForHit, navigateToHit, type SearchPaletteVariant } from './searchNavigation';

const MAX_PER_GROUP = 5;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

export type { SearchPaletteVariant };

/**
 * The global search palette (`AM-132 point 10–11`, 13 §6.1): `Ctrl+K`/`Cmd+K`, `/` outside a text
 * control, or a click on the header's search box (`useSearchShortcut`, `ConsoleHeader`). Results
 * come straight from `GET /search`, already filtered and re-verified for this actor - the portal
 * only ever gets its own requests and `customer` articles back, and this component adds no filter
 * of its own on top of that.
 */
export function SearchPalette({
  opened,
  onClose,
  variant,
}: {
  opened: boolean;
  onClose: () => void;
  variant: SearchPaletteVariant;
}) {
  const { t } = useTranslation(['search', 'common']);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, DEBOUNCE_MS);
  const trimmed = debounced.trim();
  const canSearch = trimmed.length >= MIN_QUERY_LENGTH;

  const results = useQuery({
    queryKey: ['search', 'palette', trimmed] as const,
    enabled: opened && canSearch,
    queryFn: ({ signal }) => searchApi.search({ q: trimmed, limit: 50 }, signal),
  });

  const groups = useMemo(() => groupHits(results.data?.items ?? []), [results.data]);

  const close = () => {
    setQuery('');
    onClose();
  };

  const goTo = (hit: SearchHit) => {
    close();
    navigateToHit(navigate, hit, variant);
  };

  const seeAll =
    variant === 'console' && canSearch
      ? () => {
          const q = trimmed;
          close();
          void navigate({ to: '/search', search: { q } });
        }
      : null;

  return (
    <Modal opened={opened} onClose={close} title={t('search:palette.title')} size="lg" centered>
      <Stack gap="sm">
        <TextInput
          autoFocus
          leftSection={<Icon name="search" size={16} />}
          placeholder={t(
            variant === 'portal'
              ? 'search:palette.portalPlaceholder'
              : 'search:palette.placeholder',
          )}
          aria-label={t('search:palette.placeholder')}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        <Stack gap="xs" mah={420} style={{ overflowY: 'auto' }}>
          {!canSearch ? (
            <Text c="dimmed" size="sm">
              {t('search:palette.prompt')}
            </Text>
          ) : (
            // Guía única, reglas 8.1 a 8.3: the indicator while it searches, the notice when the search
            // could not be read, and "Sin resultados." only when it answered with nothing.
            <ReadingState
              what={t('search:palette.what')}
              isLoading={results.isPending}
              error={results.error}
              onRetry={() => void results.refetch()}
              isEmpty={groups.length === 0}
              empty={
                <Text c="dimmed" size="sm">
                  {t('search:palette.empty')}
                </Text>
              }
            >
              {groups.map(([entityType, hits]) => (
                <div key={entityType}>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                    {t(`search:entityType.${entityType}`, { defaultValue: entityType })}
                  </Text>
                  {hits.slice(0, MAX_PER_GROUP).map((hit) => (
                    <ResultRow
                      key={`${hit.entityType}-${hit.entityId}`}
                      hit={hit}
                      variant={variant}
                      onSelect={() => goTo(hit)}
                    />
                  ))}
                </div>
              ))}
            </ReadingState>
          )}
        </Stack>
        {seeAll === null ? null : (
          <>
            <Divider />
            <UnstyledButton onClick={seeAll}>
              <Text size="sm" fw={600} c="steel.6">
                {t('search:palette.seeAll')}
              </Text>
            </UnstyledButton>
          </>
        )}
      </Stack>
    </Modal>
  );
}

/**
 * One hit. A type the console cannot open - a `person`, which has no screen - is still shown, as
 * plain text: the mail and the phone are in the snippet, and a control that does nothing when
 * pressed is worse than no control (`AM-144` point 4).
 */
function ResultRow({
  hit,
  variant,
  onSelect,
}: {
  hit: SearchHit;
  variant: SearchPaletteVariant;
  onSelect: () => void;
}) {
  const body = (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
        <Text size="sm" fw={500} truncate>
          {hit.title}
        </Text>
        {hit.snippet === undefined || hit.snippet === null ? null : (
          <Text size="xs" c="dimmed" truncate>
            {renderHighlightedSnippet(hit.snippet)}
          </Text>
        )}
      </Stack>
      {hit.reference === null || hit.reference === '' ? null : (
        <Badge variant="light" size="sm">
          {hit.reference}
        </Badge>
      )}
    </Group>
  );

  return hrefForHit(hit, variant) === undefined ? (
    <div style={{ paddingBlock: 6 }}>{body}</div>
  ) : (
    <UnstyledButton
      onClick={onSelect}
      py={6}
      style={{ display: 'block', width: '100%', borderRadius: 'var(--sl-radius-1)' }}
    >
      {body}
    </UnstyledButton>
  );
}

/**
 * Groups in the order of 13 §6.1. What the order list does **not** name is appended instead of
 * dropped: this component used to intersect the response with that list, so every type the server
 * gained after it was written - `person` and `invoice`, indexed and answered since migration 0022 -
 * reached the browser and was thrown away before it could be drawn (`AM-144` point 1).
 */
function groupHits(hits: readonly SearchHit[]): Array<[SearchEntityType, SearchHit[]]> {
  const byType = new Map<SearchEntityType, SearchHit[]>();
  for (const hit of hits) {
    const list = byType.get(hit.entityType) ?? [];
    list.push(hit);
    byType.set(hit.entityType, list);
  }
  const known = SEARCH_ENTITY_TYPE_ORDER.filter((type) => byType.has(type));
  const rest = [...byType.keys()].filter((type) => !SEARCH_ENTITY_TYPE_ORDER.includes(type));
  return [...known, ...rest].map((type) => [type, byType.get(type)!]);
}
